/* eslint-disable no-param-reassign */
import React, { useRef, useEffect, useState, useMemo } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { makeStyles } from '@material-ui/core/styles';
import { InfoOutlined } from '@material-ui/icons';
import { CircularProgress, Grid, Typography, Icon } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useInfiniteQuerySalesStream, fetchSalesStreamTargetHeld } from '../../hooks';
import SalesStreamContentPanel from '../../components/SalesStream/Content';
import SalesStreamPanel from '../../components/SalesStream/Panel';
import { getUserId, getDropdownUserOrg } from '../../helpers/user';
import {
  salesStreamSorting,
  mutateSalesData,
  checkIfTargetAvailable,
  summaryCalculation,
  detailsCalculation,
  hasCotaAtFloorAccess,
} from '../../helpers/sales-stream';
import sortLowToHigh from '../../images/sales-stream/icons/sortLowToHigh.svg';
import sortHighToLow from '../../images/sales-stream/icons/sortHighToLow.svg';
import { addSaleNotification } from '../../redux/actions/sale-notifications';

const useStyles = makeStyles({
  salesStreamContent: {
    padding: '0px 20px',
    margin: '0px 10px 0px 20px',
  },
  content: {
    padding: '15px 20px',
    backgroundColor: '#2A2D3A',
    border: '2px solid #2A2D3A',
    borderRadius: '13px',
  },
  salesStreamContentPanelOpen: {
    marginLeft: '215px !important',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
  },
  boldText: {
    fontWeight: 'bold',
    marginLeft: '10px',
    fontSize: '15px',
  },
  normalText: {
    fontWeight: 'normal',
  },
  space: {
    marginLeft: '4px', // Adds space between "Welcome back," and "User!"
  },
  noInfoLabel: {
    color: '#E9E9E9',
    marginLeft: '5px',
  },
  noInfoIcon: {
    color: '#E9E9E9',
    marginTop: '-3px',
  },
  toggleButton: {
    margin: '0px 5px !important',
    lineHeight: '14.52px',
    borderRadius: '17px !important',
    background: 'linear-gradient(to right, #0783FE, #9747FF)',
    color: '#F9FAFB !important',
    padding: '0px !important',
    border: '1px solid transparent !important',
    '&.Mui-selected': {
      background: 'linear-gradient(to right, #0783FE, #9747FF)',
      color: '#F9FAFB',
    },
    '&:hover': {
      background: 'linear-gradient(to right, #0783FE, #9747FF)',
      color: '#F9FAFB',
    },
  },
  toggleButtonContent: {
    display: 'flex',
    background: '#2A2D3A',
    border: '1px solid transparent',
    borderRadius: '17px',
    padding: '0px 10px',
  },
  svgIcon: {
    fill: '#F9FAFB',
    filter: 'invert(1) sepia(1) saturate(5) hue-rotate(200deg)',
    width: '20px',
    height: '20px',
    paddingLeft: '3px',
  },
  toggleGroup: {
    display: 'flex',
    justifyContent: 'center',
  },
  label: {
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    color: '#F9FAFB',
    textTransform: 'none',
    paddingRight: '2px',
  },
  sortByLable: {
    fontSize: '12px',
    lineHeight: '12px',
    color: '#DAE1F9',
    display: 'flex',
    alignItems: 'center',
  },
});

const SalesStreamContainer = ({ addNotification }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [filterSelectedValue, setFilterValue] = useState([]);
  const [showTargetHeld, setShowTargetHeld] = useState(false);
  const [sortingSelection, setSortingSelection] = useState('datetime');
  const [isGradientVisible, setIsGradientVisible] = useState(true);
  const [isFeatureAvailableForOrg, setIsFeatureAvailableForOrg] = useState(true);
  const [isRefetchBySorting, setIsRefetchBySorting] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [cotaAtFloorPercentage, setCotaAtFloorPercentage] = useState(null);
  const { WS_ENDPOINT_SS } = process.env;

  const sortingValues = useMemo(
    () => [
      { label: 'Event Time', id: 'datetime' },
      { label: '# of Tickets', id: 'tickets' },
      { label: 'COTA', id: 'cota' },
      { label: 'Slowest Target', id: 'target' },
      { label: '$ COTA at Floor', id: 'cota_at_floor' },
    ],
    []
  );

  useEffect(() => {
    if (filterSelectedValue && filterSelectedValue.length > 0) {
      if (filterSelectedValue.some(f => f.includes('organization'))) {
        const filterValues = filterSelectedValue[0].split('|');
        if (filterValues && filterValues.length > 0 && filterValues[0] === 'organization') {
          setIsFeatureAvailableForOrg(checkIfTargetAvailable(filterValues[1]));
        }
      } else {
        setIsFeatureAvailableForOrg(true);
      }
    }
  }, [filterSelectedValue]);

  const handleFilterChange = event => {
    const { value } = event.target;
    event.persist();
    setIsFirstLoad(true); // Setting first load to true to display the spinner when filter changes
    let org = null;
    if (value.length > 0) {
      if (value.some(v => v.includes('organization'))) {
        if (value.length > 1) {
          const newValue = value[1];
          org = newValue;
          setFilterValue([newValue]);
        } else {
          const newValue = value[0];
          org = newValue;
          setFilterValue(value);
        }
      } else {
        setFilterValue(value);
      }
    } else {
      setFilterValue([]);
    }
    if (org) {
      const orgValues = org.split('|');
      if (orgValues && orgValues.length > 0 && orgValues[0] === 'organization') {
        setShowTargetHeld(checkIfTargetAvailable(orgValues[1]));
      }
    }
  };

  const handleCotaAtFloorPercentageChange = value => {
    setIsFirstLoad(true);
    setCotaAtFloorPercentage(value);
  };

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(WS_ENDPOINT_SS, {
    share: true,
    shouldReconnect: () => true,
  });

  const {
    data = null,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    displayedItems,
    refetch,
    resetInfiniteQueryPagination,
  } = useInfiniteQuerySalesStream(15, filterSelectedValue, sortingSelection, cotaAtFloorPercentage);

  const observerRef = useRef();
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const useRefetchOnHourChange = () => {
    useEffect(() => {
      const now = new Date();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      const millisecondsUntilNextHour = Math.abs(60 - minutes) * 60 * 1000 - seconds * 1000;

      const timeoutId = setTimeout(() => {
        refetch();
        const intervalId = setInterval(() => {
          refetch();
        }, 60 * 60 * 1000);

        return () => clearInterval(intervalId);
      }, millisecondsUntilNextHour);

      return () => clearTimeout(timeoutId);
    }, [refetch]);
  };

  const handleSelection = (event, newValue) => {
    if (newValue !== null) {
      setSortingSelection(newValue);
      setIsRefetchBySorting(true);
      if (newValue === 'datetime') {
        setIsGradientVisible(true);
      } else {
        setIsGradientVisible(false);
      }
    }
  };

  useRefetchOnHourChange();

  useEffect(() => {
    if (!isFetching && isFirstLoad) {
      setIsFirstLoad(false);
    }
    if (isRefetchBySorting && !isFetching) {
      setIsRefetchBySorting(false);
    }
  }, [isFetching]);

  const targetHeldPercentage = (adjustedTargetHeld, exists, current) => {
    if (adjustedTargetHeld) {
      return adjustedTargetHeld < exists.available_count ? (adjustedTargetHeld / exists.available_count) * 99 : 99;
    }
    return current.target_percentage;
  };

  // Function to check datetime values
  const checkDateTime = async () => {
    const dataT = mutateSalesData(salesData, true);
    let newSalesData = [];
    if (dataT.data) {
      newSalesData = dataT.data;
    } else {
      newSalesData = salesData;
    }
    // Check target held to update only if we need to update the target held
    if (showTargetHeld && dataT.refetch && Array.isArray(dataT.refetch) && dataT.refetch.length > 0) {
      const eventRefetch = dataT.refetch;
      const newHeld = await fetchSalesStreamTargetHeld(eventRefetch);
      if (newHeld?.data) {
        const heldData = newHeld.data;
        const newHeldSalesData = newSalesData.map(i => {
          const exists = heldData.find(h => h.lobsang_event_id === i.lobsang_event_id);
          let adjustedTargetHeld;
          if (exists && exists.target_held) {
            if (exists.target_held < exists.available_count) {
              adjustedTargetHeld =
                exists.target_held % 2 === 1 && exists.target_held < 10 ? exists.target_held - 1 : exists.target_held;
            } else {
              adjustedTargetHeld =
                exists.available_count % 2 === 1 && exists.available_count < 10
                  ? exists.available_count - 1
                  : exists.available_count;
            }
          } else {
            adjustedTargetHeld = undefined;
          }

          return {
            ...i,
            target_held: adjustedTargetHeld || i.target_held,
            // Calculate target held percentage
            target_percentage: targetHeldPercentage(adjustedTargetHeld, exists, i),
          };
        });
        if (JSON.stringify(newHeldSalesData !== JSON.stringify(salesData))) {
          setSalesData(newHeldSalesData);
          return;
        }
      }
    }
    if (JSON.stringify(newSalesData) !== JSON.stringify(salesData)) {
      setSalesData(newSalesData);
    }
  };

  useEffect(() => {
    // Set interval to run checkDateTime every minute (60000 ms)
    // const intervalId = setInterval(checkDateTime, 60000);
    const intervalId = setInterval(checkDateTime, 60000);

    // Clear the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [salesData]);

  // WS Ready state connection
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendJsonMessage({
        action: 'subscribe',
        user_id: getUserId(),
        org_id: getDropdownUserOrg(),
      });
    }
  }, [readyState]);

  const updateLastDateChange = (messageSampleArray, salesDataSample) => {
    return salesDataSample.map(event => {
      const matchedMessage = messageSampleArray.find(
        messageSample => messageSample.skybox_event_id === event.skybox_event_id
      );

      if (matchedMessage) {
        return {
          ...event,
          last_date_change: matchedMessage.insertion_datetime,
          last_user_change: matchedMessage.change_type,
        };
      }

      return { ...event };
    });
  };

  // Update summary and details stats if apply
  const updateSummaryStats = message => {
    let updatedDetails = false;
    const { totals: _, ...newSummary } = summaryData;
    const { info: _1, ...newDetails } = detailsData;
    const saleMessages = message.filter(msg => !msg.topic || msg.topic !== 'PRICE_UPDATED');
    if (saleMessages) {
      saleMessages.forEach(m => {
        if (newSummary[m.skybox_event_id]) {
          newSummary[m.skybox_event_id].current_tickets = m.available_count;
        }
        if (detailsData && detailsData.stats && detailsData.stats.length > 0) {
          const detailsEvent = newDetails.stats.find(d => d.skybox_event_id === m.skybox_event_id);
          if (detailsEvent) {
            updatedDetails = true;
            detailsEvent.number_of_tickets_current = m.available_count;
          }
        }
      });
      const totals = summaryCalculation(newSummary);
      newSummary.totals = totals;
      setSummaryData(newSummary);
      if (updatedDetails) {
        const detailsStats = detailsCalculation(newDetails.stats);
        setDetailsData({ ...newDetails, info: detailsStats });
      }
    }
  };

  // WS - Received a new message from socket
  useEffect(() => {
    if (lastJsonMessage) {
      const { message, data: messageType } = lastJsonMessage;
      if (message && Array.isArray(message) && message.length > 0) {
        const priceChangeMessages = message.filter(msg => msg.topic && msg.topic === 'PRICE_UPDATED');
        if (priceChangeMessages && priceChangeMessages.length > 0) {
          const salesDataUpdated = updateLastDateChange(priceChangeMessages, salesData);
          setSalesData(salesDataUpdated);
        } else {
          const sbIds = salesData.map(i => i.skybox_event_id);
          const sbIdsWithoutCount = [];
          const existingEvents = message.filter(m => sbIds.includes(m.skybox_event_id));
          const sbIdsExisting = existingEvents.map(i => i.skybox_event_id);
          const currentSalesData = salesData;
          currentSalesData.forEach(sales => {
            if (sbIdsExisting.includes(sales.skybox_event_id)) {
              const eventData = existingEvents.find(i => i.skybox_event_id === sales.skybox_event_id);
              if (eventData) {
                sales.available_count =
                  eventData?.available_count !== undefined ? eventData.available_count : sales.available_count;
                sales.quantity_percentage =
                  eventData.quantity_percentage !== undefined
                    ? eventData.quantity_percentage
                    : sales.quantity_percentage;
                sales.cota = eventData?.cota !== undefined ? eventData.cota : sales.cota;
                sales.last_sold_ticket_cost =
                  eventData?.last_sold_ticket_cost !== undefined
                    ? eventData.last_sold_ticket_cost
                    : sales.last_sold_ticket_cost;
                sales.last_sold_ticket_price =
                  eventData?.last_sold_ticket_price !== undefined
                    ? eventData.last_sold_ticket_price
                    : sales.last_sold_ticket_price;
                // Validate if with message received the target held is now greater than the available count in order to readjust the count
                if (sales.target_held > sales.available_count) {
                  sales.target_held =
                    sales.available_count % 2 === 1 && sales.available_count < 10
                      ? sales.available_count - 1
                      : sales.available_count;
                } else {
                  sales.target_held =
                    sales.target_held % 2 === 1 && sales.target_held < 10 ? sales.target_held - 1 : sales.target_held;
                }
                sales.current_hour_ticket_sales =
                  eventData?.current_hour_ticket_sales !== undefined
                    ? eventData.current_hour_ticket_sales
                    : sales.current_hour_ticket_sales;
                sales.past_hour_ticket_sales =
                  eventData?.past_hour_ticket_sales !== undefined
                    ? eventData.past_hour_ticket_sales
                    : sales.past_hour_ticket_sales;
                if (eventData.last_sold_datetime) {
                  sales.last_sold_datetime = eventData.last_sold_datetime;
                }
                if (eventData.last_sold_marketplace) {
                  sales.last_sold_marketplace = eventData.last_sold_marketplace;
                }
                sales.slowest_target = eventData?.slowest_target ? eventData.slowest_target : sales.slowest_target;
                // This only for QA to test, since cota is mostly 0 in QA (Uncomment when testing in QA and local)
                // if (eventData.cota !== 0) {
                //  sales.cota = eventData.cota;
                // }
                if (eventData.available_count === 0) {
                  sbIdsWithoutCount.push(eventData.skybox_event_id);
                }

                // Update the COTA at Floor value
                sales.cota_at_floor = eventData?.cota_at_floor ?? sales.cota_at_floor;
                // Update the COTA at Floor percentage
                sales.percentage_cota_at_floor = eventData?.percentage_cota_at_floor ?? sales.percentage_cota_at_floor;
              }
            }
          });
          // Removing events where available count is now 0
          let finalSalesData = currentSalesData.filter(s => !sbIdsWithoutCount.includes(s.skybox_event_id));

          // Filter the sales data based on the COTA at Floor percentage
          if (cotaAtFloorPercentage !== null) {
            finalSalesData = finalSalesData.filter(sale => sale.percentage_cota_at_floor >= cotaAtFloorPercentage);
          }

          const sortedSalesData = salesStreamSorting(finalSalesData, sortingSelection);
          setSalesData(sortedSalesData.data || []);
          // Refetch if events were removed
          if (sortedSalesData.length < displayedItems.length) {
            refetch();
          }
          // Update values for summary panel
          if (summaryData && messageType === 'Events') {
            updateSummaryStats(message);
          }
        }
      }
    }
  }, [lastJsonMessage]);

  const getWebSocketEventMessage = (eventName, socketMessage) => {
    const isSocketMessageEmpty = socketMessage === null;
    const isEventMismatched = socketMessage?.data !== eventName;

    if (isSocketMessageEmpty || isEventMismatched) {
      return [];
    }

    return socketMessage.message;
  };

  // Show a notification when a sale happens
  useEffect(() => {
    const messages = getWebSocketEventMessage('Events', lastJsonMessage);
    const eventsOnScreen = salesData.map(sale => sale.skybox_event_id);

    messages.forEach(node => {
      const isOnScreen = eventsOnScreen.includes(node.skybox_event_id);
      const isFromCurrentDay = node.last_sold_datetime && node.last_sold_marketplace;
      const hasTickets = node.available_count > 0;
      const shouldAddNotification = isOnScreen && isFromCurrentDay && hasTickets;

      if (shouldAddNotification) {
        const event = salesData.find(eventNode => eventNode.skybox_event_id === node.skybox_event_id);
        const notification = {
          id: uuidv4(),
          eventId: event.lobsang_event_id,
        };

        addNotification(notification);
      }
    });
  }, [lastJsonMessage]);

  // Update the COTA at Floor percentage of the events
  useEffect(() => {
    const messages = getWebSocketEventMessage('COTA_AT_FLOOR', lastJsonMessage);
    const eventsOnScreen = salesData.map(sale => sale.skybox_event_id);

    const relevantMessages = messages.filter(message => eventsOnScreen.includes(message.skybox_event_id));

    if (relevantMessages.length > 0) {
      const updatedSalesData = salesData.map(sale => {
        const matchingMessage = relevantMessages.find(message => message.skybox_event_id === sale.skybox_event_id);

        // If there is a matching message, update the sale with the new percentage
        if (matchingMessage) {
          return {
            ...sale,
            percentage_cota_at_floor: matchingMessage.percentage_cota_at_floor,
          };
        }

        return sale;
      });

      // Filter the sales data based on the COTA at Floor percentage
      if (cotaAtFloorPercentage !== null) {
        const filteredSalesData = updatedSalesData.filter(
          sale => sale.percentage_cota_at_floor >= cotaAtFloorPercentage
        );
        setSalesData(filteredSalesData);

        // Refetch if events were removed
        if (filteredSalesData.length < displayedItems.length) {
          refetch();
        }
      } else {
        setSalesData(updatedSalesData);
      }
    }
  }, [lastJsonMessage, cotaAtFloorPercentage]);

  // For rendering more pages when scrolling down
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          if (hasNextPage) {
            fetchNextPage();
          }
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [fetchNextPage, hasNextPage]);

  useEffect(() => {
    if (data && data.pages && data.pages.length > 0) {
      let newData = [];
      let intervals = [];
      data.pages.forEach(item => {
        if (item.data) {
          const newIntervals = item.data.filter(p => p.first).map(i => i.time_diff_segment);
          const repeatedIntervals = intervals.filter(interval => newIntervals.includes(interval));
          if (intervals.length === 0) {
            intervals = [...newIntervals];
          } else {
            const nonRepeated = newIntervals.filter(newInterval => !intervals.includes(newInterval));
            intervals = [...intervals, ...nonRepeated];
          }
          const pageData = item.data
            .filter(i => i.available_count !== 0)
            .map(i => {
              const adjustedTargetHeld =
                // eslint-disable-next-line no-nested-ternary
                i.target_held < i.available_count
                  ? i.target_held % 2 === 1 && i.target_held < 10
                    ? i.target_held - 1
                    : i.target_held
                  : i.available_count % 2 === 1 && i.available_count < 10
                  ? i.available_count - 1
                  : i.available_count;

              return {
                ...i,
                first:
                  repeatedIntervals.length > 0 ? i.first && !repeatedIntervals.includes(i.time_diff_segment) : i.first,
                target_percentage:
                  adjustedTargetHeld < i.available_count ? (adjustedTargetHeld / i.available_count) * 99 : 99,
                target_held: adjustedTargetHeld,
              };
            });

          newData = [...newData, ...pageData];
        }
      });
      const dataT = mutateSalesData(newData, true);
      setSalesData(dataT?.data);
    }
  }, [data]);

  useEffect(() => {
    setShowTargetHeld(checkIfTargetAvailable());
  }, []);

  useEffect(() => {
    resetInfiniteQueryPagination();
    refetch();
  }, [sortingSelection]);

  const noInfo = (
    <Grid item xs={12}>
      <Grid container>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Icon className={classes.noInfoIcon}>
            <InfoOutlined />
          </Icon>
          <Typography className={classes.noInfoLabel}>There are no events assigned to this user.</Typography>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <div className={`${classes.salesStreamContent} ${isOpen ? classes.salesStreamContentPanelOpen : ''}`}>
      <Grid container>
        <Grid item xs={1}>
          <SalesStreamPanel
            isOpen={isOpen}
            togglePanel={togglePanel}
            handleFilterChange={handleFilterChange}
            filterValue={filterSelectedValue}
            summaryData={summaryData}
            setSummaryData={setSummaryData}
            detailsData={detailsData}
            setDetailsData={setDetailsData}
            cotaAtFloorPercentage={cotaAtFloorPercentage}
            onCotaAtFloorPercentageChange={handleCotaAtFloorPercentageChange}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.content}>
            {isFetching && isFirstLoad ? (
              <SalesStreamContentPanel isFetching />
            ) : (
              (salesData.length === 0 && noInfo) || (
                <>
                  <Grid container direction="row" style={{ width: '100%', marginBottom: '5px' }}>
                    <Grid item style={{ width: isGradientVisible ? '4.5%' : '1%' }} />
                    <Grid item style={{ display: 'flex' }}>
                      <Typography className={classes.sortByLable}>Sort by: </Typography>
                    </Grid>
                    <Grid item>
                      <ToggleButtonGroup
                        exclusive
                        onChange={handleSelection}
                        className={classes.toggleGroup}
                        value={sortingSelection}
                      >
                        {sortingValues
                          .filter(
                            item => item.id !== 'target' || (isFeatureAvailableForOrg && checkIfTargetAvailable())
                          )
                          .filter(item => (item.id === 'cota_at_floor' ? hasCotaAtFloorAccess() : true))
                          .map(item => (
                            <ToggleButton key={item.id} value={item.id} className={classes.toggleButton}>
                              <div
                                className={classes.toggleButtonContent}
                                style={{ background: sortingSelection === item.id ? 'transparent' : '' }}
                              >
                                <span className={classes.label}>{item.label}</span>
                                <img
                                  src={item.id === 'datetime' ? sortLowToHigh : sortHighToLow}
                                  alt={item.id}
                                  className={classes.svgIcon}
                                />
                              </div>
                            </ToggleButton>
                          ))}
                      </ToggleButtonGroup>
                    </Grid>
                  </Grid>
                  {isRefetchBySorting ? (
                    <SalesStreamContentPanel isFetching />
                  ) : (
                    salesData.map(row => (
                      <SalesStreamContentPanel
                        key={row.lobsang_event_id}
                        name={row.name}
                        tickets={row.available_count}
                        cota={row.cota}
                        cotaAtFloorPercentage={row.percentage_cota_at_floor}
                        lastSaleDate={row.last_sold_datetime}
                        eventId={row.lobsang_event_id}
                        isFirst={row.first}
                        datetime={row.datetime}
                        beforeEvent={row.time_diff_segment}
                        heldQuantity={row.available_count}
                        heldPercentage={row.quantity_percentage}
                        target={row.target_held}
                        targetPercentage={row.target_percentage}
                        showTargetHeld={showTargetHeld}
                        currHourSales={row.current_hour_ticket_sales}
                        pastHourSales={row.past_hour_ticket_sales}
                        lastDaySales={row.last_day_ticket_sales}
                        lastDayAllCost={row.last_day_all_tickets_cost}
                        lastDayAllSales={row.last_day_all_tickets_sales}
                        sellOutHour={row.sell_out_hour}
                        lastPriceChange={row.last_date_change}
                        lastUserChange={row.last_user_change}
                        lastSoldPrice={row.last_sold_ticket_price}
                        lastSoldCost={row.last_sold_ticket_cost}
                        lastSoldMarketplace={row.last_sold_marketplace}
                        isFeatureAvailableForOrg={isFeatureAvailableForOrg}
                        isGradientVisible={isGradientVisible}
                        isPanelOpen={isOpen}
                      />
                    ))
                  )}
                </>
              )
            )}
            <div ref={observerRef}>
              {isFetchingNextPage && !isFirstLoad && (
                <Grid container>
                  <Grid item xs={6} />
                  <Grid item xs={3}>
                    <CircularProgress size={30} />
                  </Grid>
                </Grid>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

SalesStreamContainer.propTypes = {
  addNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => {
  return {
    addNotification: event => dispatch(addSaleNotification(event)),
  };
};

export default connect(null, mapDispatchToProps)(SalesStreamContainer);
